import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";

export type EncounterMrnStatus = "found" | "not found" | "unknown encounter" | "lookup failed";

export interface EncounterMrnResult {
  encounterId: string;
  providerId: string | null;
  mrn: string | null;
  status: EncounterMrnStatus;
}

interface LookupResponse {
  results: EncounterMrnResult[];
}

const ENCOUNTER_MRN_LOOKUP_URL = `${QA_API_DOMAIN}/encounter-mrn-lookup`;

/**
 * The classifier ALB's WAF runs AWSManagedRulesCommonRuleSet, whose
 * `SizeRestrictions_BODY` rule blocks any request body over 8 KB with a
 * bodiless 403 (which the browser reports as a CORS failure). A uuid costs
 * 39 bytes in the JSON array, so the hard ceiling is 209 ids per request.
 */
export const MAX_ENCOUNTER_IDS_PER_REQUEST = 150;

function chunk<T>(items: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, (index + 1) * size),
  );
}

async function lookupBatch(encounterIds: string[]): Promise<EncounterMrnResult[]> {
  const res = await fetchWithSupabaseAuth(ENCOUNTER_MRN_LOOKUP_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ encounter_ids: encounterIds }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Lookup failed (${res.status})`);
  }

  const data = (await res.json()) as LookupResponse;
  return data.results;
}

export async function lookupEncounterMrns(encounterIds: string[]): Promise<EncounterMrnResult[]> {
  const batches = chunk(encounterIds, MAX_ENCOUNTER_IDS_PER_REQUEST);
  const batchResults = await Promise.all(batches.map(lookupBatch));

  return batchResults.flat();
}
